import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, IndianRupee, Package, Search, X, Download, FileText, FileSpreadsheet, Printer, MessageCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { DateField } from "@/components/DateField";
import api, { fmt, fmtDate, errText, openInNewTab, BACKEND_URL } from "@/lib/api";

const isoToday = () => new Date().toLocaleDateString("en-CA");
const STATUSES = ["New", "With Karigar", "Ready", "In Shop", "Delivered", "Cancelled"];
const DONE = ["Ready", "In Shop", "Delivered"];

const priorityOf = (it) => {
  const p = it.product;
  if (["Delivered", "Cancelled"].includes(p.status) || !it.delivery_date) return { label: "Normal", cls: "text-muted-foreground" };
  const diff = Math.round((new Date(it.delivery_date) - new Date(isoToday())) / 86400000);
  if (diff < 0) return { label: "Overdue", cls: "text-red-500 font-medium" };
  if (diff <= 2) return { label: "Urgent", cls: "text-amber-500 font-medium" };
  return { label: "Normal", cls: "text-muted-foreground" };
};

const enrich = (it) => {
  const p = it.product;
  const qty = p.quantity || 1;
  return {
    ...it,
    revenue: (p.customer_price || 0) * qty,
    karigarCost: (p.karigar_cost || 0) * qty,
    materialCost: ((p.fabric_cost || 0) + (p.stitching_cost || 0)) * qty,
    profit: p.profit || 0,
    priority: priorityOf(it),
  };
};

export default function KarigarProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [payment, setPayment] = useState({ amount: "", remarks: "", mode: "Cash" });
  const [f, setF] = useState({ q: "", customer: "All", category: "All", status: "All", priority: "All", dateField: "sent_date", from: "", to: "", minProfit: "", maxProfit: "" });
  const [exporting, setExporting] = useState("");
  const [scope, setScope] = useState("all");

  const { data: k, isLoading } = useQuery({ queryKey: ["karigar", id], queryFn: async () => (await api.get(`/karigars/${id}`)).data });

  const addPayment = useMutation({
    mutationFn: async () => (await api.post(`/karigars/${id}/payments`, { amount: Number(payment.amount), remarks: payment.remarks, mode: payment.mode })).data,
    onSuccess: () => {
      toast.success("Payment recorded");
      qc.invalidateQueries({ queryKey: ["karigar", id] });
      qc.invalidateQueries({ queryKey: ["karigars"] });
      setPayOpen(false);
      setPayment({ amount: "", remarks: "", mode: "Cash" });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const items = useMemo(() => (k?.work_items || []).map(enrich), [k]);

  const stats = useMemo(() => {
    const act = items.filter((i) => i.product.status !== "Cancelled");
    const assigned = act.filter((i) => i.product.status === "With Karigar");
    const completed = act.filter((i) => DONE.includes(i.product.status));
    const overdue = assigned.filter((i) => i.priority.label === "Overdue");
    const today = isoToday();
    const week = new Date(); week.setDate(week.getDate() - 7);
    const weekIso = week.toLocaleDateString("en-CA");
    const month = today.slice(0, 7);
    const workOn = (dates) => act.filter((i) => dates(i.product.sent_date) || dates(i.product.received_date)).length;
    const revenue = completed.reduce((s, i) => s + i.revenue, 0);
    const kCost = completed.reduce((s, i) => s + i.karigarCost, 0);
    const profit = completed.reduce((s, i) => s + i.profit, 0);
    const dispatches = k?.production_items || [];
    const prodPending = dispatches.reduce((s, d) => s + Math.max((d.sent || 0) - (d.received || 0), 0), 0);
    const prodDone = dispatches.reduce((s, d) => s + (d.received || 0), 0);
    const prodWork = (dates) => dispatches.filter((d) => dates(d.sent_date)).reduce((s, d) => s + (d.sent || 0), 0);
    const inc = (o, p) => (scope === "orders" ? o : scope === "production" ? p : o + p);
    return {
      assigned: assigned.length + prodPending,
      completed: inc(completed.length, prodDone),
      pending: inc(assigned.length, prodPending),
      overdue: overdue.length,
      revenue, kCost, profit,
      margin: revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : "0",
      today: inc(workOn((d) => d === today), prodWork((d) => d === today)),
      weekly: inc(workOn((d) => d && d >= weekIso), prodWork((d) => d && d >= weekIso)),
      monthly: inc(workOn((d) => d && d.startsWith(month)), prodWork((d) => d && d.startsWith(month))),
    };
  }, [items, k, scope]);

  const profitStats = useMemo(() => {
    const done = items.filter((i) => DONE.includes(i.product.status));
    const rev = done.reduce((s, i) => s + i.revenue, 0);
    const profit = done.reduce((s, i) => s + i.profit, 0);
    const sorted = [...done].sort((a, b) => b.profit - a.profit);
    return {
      revenue: rev,
      karigarCost: done.reduce((s, i) => s + i.karigarCost, 0),
      materialCost: done.reduce((s, i) => s + i.materialCost, 0),
      otherExpenses: 0,
      profit,
      avg: done.length ? profit / done.length : 0,
      highest: sorted[0], lowest: sorted[sorted.length - 1],
      pct: rev > 0 ? ((profit / rev) * 100).toFixed(1) : "0",
      count: done.length,
    };
  }, [items]);

  const chartData = useMemo(() => {
    const byMonth = {};
    items.forEach((i) => {
      if (i.product.status === "Cancelled") return;
      const m = (i.order_date || "").slice(0, 7);
      if (!m) return;
      byMonth[m] = byMonth[m] || { month: m, profit: 0, revenue: 0, completed: 0, pending: 0 };
      if (DONE.includes(i.product.status)) {
        byMonth[m].profit += i.profit;
        byMonth[m].revenue += i.revenue;
        byMonth[m].completed += 1;
      } else {
        byMonth[m].pending += 1;
      }
    });
    return Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month)).slice(-12);
  }, [items]);

  const hasFilters = f.q || f.customer !== "All" || f.category !== "All" || f.status !== "All" || f.priority !== "All" || f.from || f.to || f.minProfit || f.maxProfit;
  const filtered = items.filter((i) => {
    const p = i.product;
    if (f.customer !== "All" && i.customer_name !== f.customer) return false;
    if (f.category !== "All" && p.category !== f.category) return false;
    if (f.status !== "All" && p.status !== f.status) return false;
    if (f.priority !== "All" && i.priority.label !== f.priority) return false;
    const d = f.dateField === "sent_date" ? p.sent_date : (f.dateField === "order_date" ? i.order_date : i.delivery_date);
    if (f.from && (!d || d < f.from)) return false;
    if (f.to && (!d || d > f.to)) return false;
    if (f.minProfit !== "" && i.profit < Number(f.minProfit)) return false;
    if (f.maxProfit !== "" && i.profit > Number(f.maxProfit)) return false;
    if (f.q) {
      const hay = [p.name, p.category, i.order_number, i.customer_name, p.remarks].join(" ").toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    return true;
  });

  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/karigars/${id}/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${k.name}-production.${format === "excel" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting("");
    }
  };

  const openSlip = () => {
    setSlipPreset("month");
    setSlipSel(null);
    setSlipOpen(true);
  };

  const [slipOpen, setSlipOpen] = useState(false);
  const [slipPreset, setSlipPreset] = useState("month");
  const [slipRange, setSlipRange] = useState({ from: isoToday(), to: isoToday() });
  const [slipSel, setSlipSel] = useState(null);

  const slipDates = useMemo(() => {
    const now = new Date();
    const iso = (d) => d.toLocaleDateString("en-CA");
    const t = iso(now);
    const back = (n) => iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - n));
    switch (slipPreset) {
      case "today": return { from: t, to: t };
      case "yesterday": return { from: back(1), to: back(1) };
      case "last2": return { from: back(1), to: t };
      case "week": return { from: back((now.getDay() + 6) % 7), to: t };
      case "month": return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: t };
      default: return slipRange;
    }
  }, [slipPreset, slipRange]);

  const { data: slipData, isFetching: slipLoading } = useQuery({
    queryKey: ["slip-records", id, slipDates.from, slipDates.to],
    queryFn: async () => (await api.get(`/karigars/${id}/slip-records`, { params: { from: slipDates.from, to: slipDates.to } })).data,
    enabled: slipOpen,
  });
  useEffect(() => { setSlipSel(null); }, [slipDates.from, slipDates.to]);

  const slipRecords = slipData?.records || [];
  const selectedKeys = slipSel === null ? slipRecords.map((r) => r.key) : slipSel;
  const toggleKey = (key) => {
    const cur = new Set(selectedKeys);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setSlipSel([...cur]);
  };
  const selRecords = slipRecords.filter((r) => selectedKeys.includes(r.key));
  const selTotal = selRecords.reduce((s, r) => s + r.amount, 0);

  const downloadPeriodSlip = () => {
    const sel = slipSel === null ? "" : selectedKeys.join(",");
    openInNewTab(async () => {
      const res = await api.get(`/karigars/${id}/slip`, { params: { from: slipDates.from, to: slipDates.to, sel }, responseType: "blob" });
      return URL.createObjectURL(res.data);
    }).catch(() => toast.error("Slip generate nahi hui"));
  };

  const sendPeriodSlipWhatsApp = () => {
    openInNewTab(async () => {
      const sel = slipSel === null ? "" : selectedKeys.join(",");
      const { data } = await api.post(`/karigars/${id}/slip-link`, { from: slipDates.from, to: slipDates.to, sel });
      let digits = (data.phone || "").replace(/\D/g, "");
      if (!digits) { toast.error("Karigar ka phone number nahi hai — Edit karke add karein"); return null; }
      if (digits.length === 10) digits = "91" + digits;
      const url = `${BACKEND_URL}/api/s/${data.code}`;
      const msg = [
        `*ANAMTA NOOR*`,
        `_Zari Handwork · Handcrafted Elegance_`,
        ``,
        `Dear *${k?.name}*,`,
        `Aapke kaam ki slip (${fmtDate(slipDates.from)} — ${fmtDate(slipDates.to)}):`,
        ``,
        `Pieces: ${selRecords.reduce((s, r) => s + (r.sent || 0), 0)}`,
        `*Total Amount: ${fmt(selTotal)}*`,
        `Paid: ${fmt(slipData?.paid || 0)}`,
        `*Pending Balance: ${fmt(slipData?.pending || 0)}*`,
        ``,
        `*Work Slip (PDF):*`,
        url,
        ``,
        `Dhanyavaad,`,
        `*Anamta Noor*`,
      ].join("\n");
      return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    }).catch(() => toast.error("WhatsApp link nahi ban paya"));
  };

  const sendSlipWhatsApp = () => {
    openInNewTab(async () => {
      const { data } = await api.get(`/karigars/${id}/whatsapp`);
      let digits = (data.phone || "").replace(/\D/g, "");
      if (!digits) { toast.error("Karigar ka phone number nahi hai — Edit karke add karein"); return null; }
      if (digits.length === 10) digits = "91" + digits;
      const slipUrl = `${BACKEND_URL}/api/s/${data.code}`;
      const pendingOrders = items.filter((i) => i.product.status === "With Karigar");
      const orderLines = pendingOrders.map((i, n) => `  ${n + 1}. ${i.product.name}${i.product.quantity > 1 ? ` (×${i.product.quantity})` : ""} — ${i.order_number}`);
      const prodPend = (k.production_items || []).filter((d) => (d.sent || 0) - (d.received || 0) > 0);
      const prodLines = prodPend.map((d, n) => `  ${n + 1}. ${d.design_name} — ${(d.sent || 0) - (d.received || 0)} pcs pending`);
      const msg = [
        `*ANAMTA NOOR*`,
        `_Zari Handwork · Handcrafted Elegance_`,
        ``,
        `Dear *${k.name}*,`,
        `Aapke kaam ki detail:`,
        ``,
        orderLines.length ? `*Pending Order Work (${pendingOrders.reduce((s, i) => s + (i.product.quantity || 1), 0)} pcs):*` : null,
        ...orderLines,
        orderLines.length ? `` : null,
        prodLines.length ? `*Pending Shop Production:*` : null,
        ...prodLines,
        prodLines.length ? `` : null,
        !orderLines.length && !prodLines.length ? `Abhi koi pending kaam nahi hai.` : null,
        !orderLines.length && !prodLines.length ? `` : null,
        `*Payment Summary*`,
        `  Total Work: ${fmt(k.total_work)}`,
        `  Paid: ${fmt(k.paid)}`,
        k.pending > 0 ? `  *Pending Balance: ${fmt(k.pending)}*` : `  *Fully Paid*`,
        ``,
        `*Work Slip (PDF):*`,
        slipUrl,
        ``,
        `Dhanyavaad,`,
        `*Anamta Noor*`,
      ].filter((l) => l !== null).join("\n");
      return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    }).catch(() => toast.error("WhatsApp link nahi ban paya"));
  };

  if (isLoading || !k) return <div className="h-64 rounded-xl bg-muted animate-pulse" />;

  const uniq = (fn) => [...new Set(items.map(fn).filter(Boolean))].sort();
  const ledger = [...k.payments].sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.created_at || "").localeCompare(b.created_at || ""));
  let running = k.total_work;
  const ledgerRows = ledger.map((p) => { running -= p.amount; return { ...p, balance: Math.max(running, 0) }; }).reverse();

  const moneyCards = [
    ["Overdue", stats.overdue, stats.overdue > 0 ? "text-red-500" : ""],
    ["Revenue Generated", fmt(stats.revenue)], ["Karigar Cost", fmt(stats.kCost)],
    ["Net Profit", fmt(stats.profit), "text-emerald-600"], ["Profit Margin", `${stats.margin}%`, "text-emerald-600"],
  ];
  const cards = [
    ["Current Assigned", stats.assigned], ["Completed", stats.completed, "text-green-600"],
    ["Pending", stats.pending, "text-amber-500"],
    ...(scope === "production" ? [] : moneyCards),
    ["Today's Work", stats.today], ["Weekly Work", stats.weekly], ["Monthly Work", stats.monthly],
    ["Pending Payment", fmt(k.pending), k.pending > 0 ? "text-red-500" : ""],
  ];

  return (
    <div className="space-y-6" data-testid="karigar-profile-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-button" onClick={() => navigate("/karigars")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{k.name}</h1>
          <p className="text-sm text-muted-foreground">{k.phone || "No phone"} · {items.length} total products</p>
        </div>
        <Button data-testid="export-csv-button" variant="outline" size="sm" className="rounded-full" disabled={!!exporting} onClick={() => doExport("csv")}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
        </Button>
        <Button data-testid="export-excel-button" variant="outline" size="sm" className="rounded-full" disabled={!!exporting} onClick={() => doExport("excel")}>
          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
        </Button>
        <Button data-testid="export-pdf-button" variant="outline" size="sm" className="rounded-full" disabled={!!exporting} onClick={() => doExport("pdf")}>
          <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
        </Button>
        <Button data-testid="karigar-slip-button" variant="outline" size="sm" className="rounded-full border-amber-500/40 text-amber-600 hover:text-amber-600" onClick={openSlip}>
          <Printer className="h-3.5 w-3.5 mr-1.5" /> Slip
        </Button>
        <Button data-testid="karigar-whatsapp-button" size="sm" className="rounded-full bg-green-600 hover:bg-green-700 text-white" onClick={sendSlipWhatsApp}>
          <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
        </Button>
        <Button data-testid="pay-karigar-button" className="rounded-full active:scale-95 transition-transform" onClick={() => setPayOpen(true)}>
          <IndianRupee className="h-4 w-4 mr-1" /> Pay Karigar
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="karigar-scope-tabs">
        {[["all", "All (Combined)"], ["production", "Only Shop Production"], ["orders", "Only Orders"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`scope-tab-${v}`}
            onClick={() => setScope(v)}
            className={`h-8 px-4 rounded-full text-xs font-medium border transition-colors ${scope === v ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="karigar-analytics-cards">
        {cards.map(([label, value, cls]) => (
          <div key={label} className="rounded-xl border bg-card p-3.5">
            <p className={`font-heading text-lg font-semibold ${cls || ""}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="production" data-testid="karigar-tabs">
        <TabsList className="rounded-full">
          {["production", "orders", "payments", "ledger", "profit"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="rounded-full capitalize">{t === "profit" ? "Profit Analysis" : t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="orders" className="space-y-4 mt-4">
          <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="production-filters">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input data-testid="production-search" className="pl-8 h-9" placeholder="Search product, order #, customer..." value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
              </div>
              {[["customer", "All Customers", uniq((i) => i.customer_name)],
                ["category", "All Categories", uniq((i) => i.product.category)],
                ["status", "All Status", STATUSES],
                ["priority", "All Priority", ["Overdue", "Urgent", "Normal"]]].map(([key, all, opts]) => (
                <Select key={key} value={f[key]} onValueChange={(v) => setF({ ...f, [key]: v })}>
                  <SelectTrigger data-testid={`production-filter-${key}`} className="w-[140px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{["All", ...opts].map((o) => <SelectItem key={o} value={o}>{o === "All" ? all : o}</SelectItem>)}</SelectContent>
                </Select>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={f.dateField} onValueChange={(v) => setF({ ...f, dateField: v })}>
                <SelectTrigger data-testid="production-filter-datefield" className="w-[150px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sent_date">Assigned Date</SelectItem>
                  <SelectItem value="order_date">Booking Date</SelectItem>
                  <SelectItem value="delivery_date">Delivery Date</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-[160px]"><DateField testId="production-filter-from" value={f.from} onChange={(v) => setF({ ...f, from: v })} /></div>
              <span className="text-xs text-muted-foreground">to</span>
              <div className="w-[160px]"><DateField testId="production-filter-to" value={f.to} onChange={(v) => setF({ ...f, to: v })} /></div>
              <Input data-testid="production-filter-minprofit" type="number" className="w-[110px] h-9" placeholder="Min profit" value={f.minProfit} onChange={(e) => setF({ ...f, minProfit: e.target.value })} />
              <Input data-testid="production-filter-maxprofit" type="number" className="w-[110px] h-9" placeholder="Max profit" value={f.maxProfit} onChange={(e) => setF({ ...f, maxProfit: e.target.value })} />
              {hasFilters && (
                <Button variant="ghost" size="sm" data-testid="production-clear-filters" className="h-9 text-muted-foreground" onClick={() => setF({ q: "", customer: "All", category: "All", status: "All", priority: "All", dateField: "sent_date", from: "", to: "", minProfit: "", maxProfit: "" })}>
                  <X className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-xl border bg-card overflow-x-auto">
            {filtered.length === 0 ? (
              <p className="px-6 py-10 text-sm text-muted-foreground text-center">No products match.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {["Product", "Customer", "Order #", "Category", "Qty", "Status", "Assigned", "Delivery", "Price", "Karigar Cost", "Material", "Est. Profit", "Actual Profit", "Priority"].map((h) => (
                      <TableHead key={h} className={["Qty", "Price", "Karigar Cost", "Material", "Est. Profit", "Actual Profit"].includes(h) ? "text-right" : ""}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((i) => (
                    <TableRow key={i.product.id} data-testid={`production-row-${i.product.id}`} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/products/${i.order_id}/${i.product.id}`)}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          {i.product.photo ? (
                            <AuthImage src={`/orders/${i.order_id}/products/${i.product.id}/photo?v=${(i.product.photo.storage_path || "").slice(-8)}`} alt={i.product.name} className="h-8 w-8 rounded-lg object-cover border shrink-0" />
                          ) : (
                            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                              <Package className="h-3.5 w-3.5 text-amber-500" />
                            </div>
                          )}
                          <span className="font-medium">{i.product.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{i.customer_name}</TableCell>
                      <TableCell className="font-medium">{i.order_number}</TableCell>
                      <TableCell className="text-muted-foreground">{i.product.category || "—"}</TableCell>
                      <TableCell className="text-right">{i.product.quantity}</TableCell>
                      <TableCell><StatusBadge status={i.product.status} /></TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(i.product.sent_date)}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(i.delivery_date)}</TableCell>
                      <TableCell className="text-right">{fmt(i.revenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{fmt(i.karigarCost)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{fmt(i.materialCost)}</TableCell>
                      <TableCell className={`text-right ${i.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(i.profit)}</TableCell>
                      <TableCell className={`text-right ${DONE.includes(i.product.status) ? (i.profit >= 0 ? "text-emerald-600 font-medium" : "text-red-500") : "text-muted-foreground"}`}>
                        {DONE.includes(i.product.status) ? fmt(i.profit) : "—"}
                      </TableCell>
                      <TableCell><span className={i.priority.cls}>{i.priority.label}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="production" className="mt-4">
          <div className="rounded-xl border bg-card overflow-x-auto" data-testid="shop-production-items">
            {(k.production_items || []).length === 0 ? (
              <p className="px-6 py-10 text-sm text-muted-foreground text-center">No shop production work assigned to this karigar yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Design</TableHead><TableHead>Code</TableHead><TableHead>Category</TableHead>
                    <TableHead>Sent Date</TableHead><TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Received</TableHead><TableHead className="text-right">Pending</TableHead>
                    <TableHead>Progress</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {k.production_items.map((d, i) => {
                    const pending = Math.max((d.sent || 0) - (d.received || 0), 0);
                    const pct = d.sent > 0 ? Math.round(((d.received || 0) / d.sent) * 100) : 0;
                    return (
                      <TableRow key={`${d.production_id}-${i}`} data-testid={`production-dispatch-${i}`} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate("/production")}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                              <Package className="h-3.5 w-3.5 text-violet-500" />
                            </div>
                            <span className="font-medium">{d.design_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{d.design_code || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{d.category || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{fmtDate(d.sent_date || d.production_date)}</TableCell>
                        <TableCell className="text-right">{d.sent}</TableCell>
                        <TableCell className="text-right text-green-600">{d.received}</TableCell>
                        <TableCell className={`text-right ${pending > 0 ? "text-amber-500 font-medium" : "text-muted-foreground"}`}>{pending}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-[120px]">
                            <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{pct}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">{fmt(d.rate)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(d.amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="rounded-xl border bg-card overflow-x-auto">
            {k.payments.length === 0 ? <p className="px-6 py-8 text-sm text-muted-foreground">No payments made yet.</p> : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead>Reason</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {k.payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-muted-foreground">{fmtDate(p.date)}</TableCell>
                      <TableCell>{p.remarks || "Payment"}</TableCell>
                      <TableCell className="text-muted-foreground">{p.mode || "—"}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{fmt(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <div className="rounded-xl border bg-card overflow-x-auto">
            {ledgerRows.length === 0 ? <p className="px-6 py-8 text-sm text-muted-foreground">No ledger entries yet.</p> : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead>Payment Reason</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Running Balance</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerRows.map((p) => (
                    <TableRow key={p.id} data-testid={`ledger-row-${p.id}`}>
                      <TableCell className="text-muted-foreground">{fmtDate(p.date)}</TableCell>
                      <TableCell>{p.remarks || "Payment"}</TableCell>
                      <TableCell className="text-muted-foreground">{p.mode || "—"}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{fmt(p.amount)}</TableCell>
                      <TableCell className={`text-right ${p.balance > 0 ? "text-red-500" : "text-muted-foreground"}`}>Balance {fmt(p.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="profit" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profit-analysis-cards">
            {[["Total Revenue", fmt(profitStats.revenue)],
              ["Total Karigar Cost", fmt(profitStats.karigarCost)],
              ["Total Material Cost", fmt(profitStats.materialCost)],
              ["Other Expenses", fmt(profitStats.otherExpenses)],
              ["Total Net Profit", fmt(profitStats.profit), "text-emerald-600"],
              ["Profit Percentage", `${profitStats.pct}%`, "text-emerald-600"],
              ["Avg Profit / Product", fmt(profitStats.avg), "text-emerald-600"],
              ["Completed Products", profitStats.count]].map(([l, v, cls]) => (
              <div key={l} className="rounded-xl border bg-card p-4">
                <p className={`font-heading text-lg font-semibold ${cls || ""}`}>{v}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {profitStats.highest && (
              <div className="rounded-xl border bg-card p-5">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Highest Profit Product</p>
                <p className="font-medium">{profitStats.highest.product.name}</p>
                <p className="text-sm text-muted-foreground">{profitStats.highest.order_number} · {profitStats.highest.customer_name}</p>
                <p className="font-heading text-xl font-semibold text-emerald-600 mt-1">{fmt(profitStats.highest.profit)}</p>
              </div>
            )}
            {profitStats.lowest && (
              <div className="rounded-xl border bg-card p-5">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Lowest Profit Product</p>
                <p className="font-medium">{profitStats.lowest.product.name}</p>
                <p className="text-sm text-muted-foreground">{profitStats.lowest.order_number} · {profitStats.lowest.customer_name}</p>
                <p className={`font-heading text-xl font-semibold mt-1 ${profitStats.lowest.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(profitStats.lowest.profit)}</p>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="profit-charts">
            {[["Monthly Profit", "profit", "#B08D42"], ["Monthly Revenue", "revenue", "#10B981"],
              ["Completed Products", "completed", "#3B82F6"], ["Pending Products", "pending", "#F59E0B"]].map(([title, key, color]) => (
              <div key={key} className="rounded-xl border bg-card p-5">
                <p className="text-sm font-medium mb-4">{title}</p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={55} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey={key} fill={color} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Pay {k.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount ₹ (Pending: {fmt(k.pending)})</Label>
              <Input data-testid="karigar-payment-amount-input" type="number" min="0" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Payment Mode</Label>
              <Select value={payment.mode} onValueChange={(v) => setPayment({ ...payment, mode: v })}>
                <SelectTrigger data-testid="karigar-payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "UPI", "Bank Transfer", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
              <Input data-testid="karigar-payment-remarks-input" value={payment.remarks} onChange={(e) => setPayment({ ...payment, remarks: e.target.value })} />
            </div>
            <Button data-testid="karigar-payment-save-button" className="w-full rounded-lg" disabled={!Number(payment.amount) || addPayment.isPending} onClick={() => addPayment.mutate()}>
              Save Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={slipOpen} onOpenChange={setSlipOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="slip-builder-dialog">
          <DialogHeader><DialogTitle className="font-heading">Karigar Slip — {k.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {[["today", "Today"], ["yesterday", "Yesterday"], ["last2", "Last 2 Days"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([v, l]) => (
                <button key={v} data-testid={`slip-preset-${v}`} onClick={() => setSlipPreset(v)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${slipPreset === v ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                  {l}
                </button>
              ))}
            </div>
            {slipPreset === "custom" && (
              <div className="flex items-center gap-2">
                <div className="w-[160px]"><DateField testId="slip-from-date" value={slipRange.from} onChange={(v) => setSlipRange({ ...slipRange, from: v })} /></div>
                <span className="text-xs text-muted-foreground">to</span>
                <div className="w-[160px]"><DateField testId="slip-to-date" value={slipRange.to} onChange={(v) => setSlipRange({ ...slipRange, to: v })} /></div>
              </div>
            )}

            <div className="rounded-lg border overflow-x-auto" data-testid="slip-records-table">
              {slipLoading ? (
                <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">Loading...</div>
              ) : slipRecords.length === 0 ? (
                <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">Is period me koi kaam nahi mila</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-9">
                        <Checkbox data-testid="slip-select-all" checked={selectedKeys.length === slipRecords.length && slipRecords.length > 0}
                          onCheckedChange={(v) => setSlipSel(v ? null : [])} />
                      </TableHead>
                      <TableHead>Design / Product</TableHead>
                      <TableHead className="text-right">Pcs</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slipRecords.map((r) => (
                      <TableRow key={r.key} data-testid={`slip-record-${r.key}`} className="cursor-pointer" onClick={() => toggleKey(r.key)}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox data-testid={`slip-check-${r.key}`} checked={selectedKeys.includes(r.key)} onCheckedChange={() => toggleKey(r.key)} />
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{r.design_name}</span>
                          {r.order_number && <span className="text-xs text-muted-foreground"> · {r.order_number}</span>}
                          {r.type === "production" && <span className="text-[10px] uppercase tracking-wide ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Production</span>}
                        </TableCell>
                        <TableCell className="text-right">{r.sent}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{r.sent_date ? fmtDate(r.sent_date) : "—"}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{r.received_date ? fmtDate(r.received_date) : "—"}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{fmt(r.rate)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="rounded-lg border bg-muted/30 p-4 grid grid-cols-3 gap-3 text-sm" data-testid="slip-preview-totals">
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Amount</p>
                <p className="font-heading text-lg font-semibold">{fmt(selTotal)}</p>
                <p className="text-[11px] text-muted-foreground">{selRecords.length} of {slipRecords.length} selected · {selRecords.reduce((s, r) => s + (r.sent || 0), 0)} pcs</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Paid Amount</p>
                <p className="font-heading text-lg font-semibold text-emerald-600">{fmt(slipData?.paid || 0)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Pending Amount</p>
                <p className="font-heading text-lg font-semibold text-red-500">{fmt(slipData?.pending || 0)}</p></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Slip sirf ek report hai — isse koi payment record nahi hota. Paid/Pending karigar ke poore account ke hain.</p>

            <div className="flex justify-end gap-2">
              <Button data-testid="slip-download-button" variant="outline" className="rounded-full" disabled={selRecords.length === 0} onClick={downloadPeriodSlip}>
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Preview / Download PDF
              </Button>
              <Button data-testid="slip-whatsapp-button" className="rounded-full bg-green-600 hover:bg-green-700 text-white" disabled={selRecords.length === 0} onClick={sendPeriodSlipWhatsApp}>
                <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Send on WhatsApp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
